import {
  Button,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { formatDate, formatHours } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

export const DAY_ENTRIES_PATH = '/admin/v1/payroll/day-entries';

/** One hand-entered person-day, as the API stores it. */
export interface TimesheetDayEntry {
  id: string;
  staffId: string;
  staffName?: string | null;
  date: string;
  kind: 'worked' | 'pto';
  /** Local "HH:MM"; an end earlier than the start crossed midnight. Null on a PTO day. */
  startTime: string | null;
  endTime: string | null;
  /** Unpaid minutes inside the shift, taken out of what the times span. */
  unpaidBreakMinutes: number;
  /** PTO, or a worked day whose times are unknown: null on PTO means "their scheduled day". */
  minutes: number | null;
  /**
   * A worked day recorded as hours alone, its shape never known — the months before anybody was on
   * the clock. Its times are null because nobody knows them, which is a different fact from a PTO
   * day having none, and only this flag can tell the two apart.
   */
  totalsOnly?: boolean;
  note: string | null;
}

/** "HH:MM" → minutes past midnight, or null while the field is empty or half-typed. */
function parseClockTime(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Minutes from clock-in to clock-out, crossing midnight when the end does not come after the
 * start — the same reading the server and the weekly schedule give the pair, because two readings
 * of "18:00 to 02:00" is one wrong number waiting to be saved. Null while either time is
 * unreadable or the two are equal (a zero-length shift is a typo, and the server refuses it).
 */
export function shiftSpanMinutes(startTime: string, endTime: string): number | null {
  const start = parseClockTime(startTime);
  const end = parseClockTime(endTime);
  if (start === null || end === null || start === end) return null;
  return end > start ? end - start : end + 1440 - start;
}

/** "10:00 to 21:00", growing "(+1 day)" when the end crosses midnight, for anywhere times print. */
export function describeShift(startTime: string, endTime: string): string {
  const start = parseClockTime(startTime);
  const end = parseClockTime(endTime);
  const crossed = start !== null && end !== null && end <= start;
  return `${startTime} to ${endTime}${crossed ? ' (+1 day)' : ''}`;
}

/**
 * "7:30", "7.5" or "8" → whole minutes; null when the text does not describe a working day.
 *
 * PTO's optional override only, since worked days became clock times. Both spellings are accepted
 * because both are how hours get said out loud, and the person typing should not have to learn
 * which one a text box happens to expect. The description under the box reads the result back
 * ("7h 30m"), so the one genuine trap — 7.30 typed to mean 7:30 — is visible before it is saved
 * rather than after it is paid.
 */
export function parseHoursInput(text: string): number | null {
  const trimmed = text.trim();
  const clock = /^(\d{1,2}):([0-5]\d)$/.exec(trimmed);
  if (clock) {
    const minutes = Number(clock[1]) * 60 + Number(clock[2]);
    return minutes > 0 && minutes <= 1440 ? minutes : null;
  }
  if (!/^\d{1,2}([.,]\d{1,2})?$/.test(trimmed)) return null;
  const minutes = Math.round(Number(trimmed.replace(',', '.')) * 60);
  return minutes > 0 && minutes <= 1440 ? minutes : null;
}

/** Stored minutes → the "7:30" spelling, for prefilling the box an entry was typed into. */
export function minutesToText(minutes: number): string {
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

const KIND_OPTIONS = [
  { value: 'worked', label: 'Worked' },
  { value: 'pto', label: 'PTO day' },
];

/**
 * Where a day nobody punched gets written down: when the shift ran, or a paid day off. One person,
 * one day, one truth — saving over a day that already has an entry replaces it, which is what "the
 * clock is wrong, I know what happened" needs. A worked day asks for the CLOCK TIMES rather than a
 * total, on the owner's correction: a total cannot say when the hours ran, and when they ran is
 * what overtime is priced from — the times are the record, whatever eventually reads them. The
 * date is fixed when the modal was opened from a day already on the sheet, and picked here when
 * the day is not on the sheet at all — the person who never punched has no row to click.
 */
export function DayEntryModal({
  staffId,
  staffName,
  period,
  initialDate,
  initialKind,
  existing,
  onClose,
  onSaved,
}: {
  staffId: string;
  staffName: string;
  period: { periodStart: string; periodEnd: string };
  /** The day being written, or null to let the person pick one inside the period. */
  initialDate: string | null;
  initialKind: 'worked' | 'pto';
  existing?: TimesheetDayEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [date, setDate] = useState<string | null>(initialDate);
  const [kind, setKind] = useState<'worked' | 'pto'>(existing?.kind ?? initialKind);
  // An entry from before times were recorded prefills nothing here: it stored only a total, and
  // inventing a start time from one would put words in the record's mouth. The note says what
  // happened; the person retypes the day as times.
  const [startTime, setStartTime] = useState(existing?.startTime ?? '');
  const [endTime, setEndTime] = useState(existing?.endTime ?? '');
  const [breakMinutes, setBreakMinutes] = useState(existing?.unpaidBreakMinutes ?? 0);
  const [hoursText, setHoursText] = useState(
    existing?.kind === 'pto' && existing.minutes != null ? minutesToText(existing.minutes) : '',
  );
  const [note, setNote] = useState(existing?.note ?? '');

  const span = shiftSpanMinutes(startTime, endTime);
  const bothTyped = parseClockTime(startTime) !== null && parseClockTime(endTime) !== null;
  // The same refusals the server would give, caught while the fields still have focus: a
  // zero-length shift is a typo, and a break at least as long as the shift leaves nothing to pay.
  const timesProblem =
    span === null
      ? bothTyped
        ? 'A shift cannot start and end at the same moment.'
        : null
      : breakMinutes >= span
        ? 'The break is longer than the shift itself.'
        : null;
  const workedMinutes = span !== null && breakMinutes < span ? span - breakMinutes : null;

  const parsed = parseHoursInput(hoursText);
  // A PTO day may leave the hours blank — blank means "their scheduled day", resolved when the
  // hours are recalculated. Hours typed but unreadable are not a record yet.
  const ptoProblem = kind === 'pto' && hoursText.trim() !== '' && parsed === null;
  const incomplete = kind === 'worked' ? workedMinutes === null : ptoProblem;

  const save = useMutation({
    mutationFn: () =>
      rawApi<TimesheetDayEntry>(
        'PUT',
        DAY_ENTRIES_PATH,
        kind === 'worked'
          ? {
              staffId,
              date,
              kind,
              startTime,
              endTime,
              unpaidBreakMinutes: breakMinutes,
              note: note.trim() || undefined,
            }
          : {
              staffId,
              date,
              kind,
              minutes: hoursText.trim() !== '' ? parsed : undefined,
              note: note.trim() || undefined,
            },
      ),
    onSuccess: () => {
      notifyDone('Day recorded', 'Recalculate the hours to bring it into the figures.');
      onSaved();
    },
    onError: (err) => notifyError(err, 'record this day'),
  });

  return (
    <Modal
      opened
      onClose={onClose}
      title={initialDate ? `${staffName} · ${formatDate(initialDate)}` : `Enter a day for ${staffName}`}
    >
      <Stack gap="md">
        {initialDate === null && (
          <DateInput
            label="Which day"
            value={date}
            onChange={(v) => setDate(v || null)}
            minDate={period.periodStart}
            maxDate={period.periodEnd}
            placeholder="Pick a day in this pay period"
            popoverProps={{ withinPortal: true }}
          />
        )}
        <SegmentedControl
          value={kind}
          onChange={(v) => setKind(v as 'worked' | 'pto')}
          data={KIND_OPTIONS}
          fullWidth
        />
        {kind === 'worked' ? (
          <>
            <Group gap="sm" align="flex-end" wrap="nowrap">
              <TimeInput
                label="Clock in"
                value={startTime}
                onChange={(e) => setStartTime(e.currentTarget.value)}
                style={{ flex: 1 }}
                autoFocus
              />
              <TimeInput
                label="Clock out"
                value={endTime}
                onChange={(e) => setEndTime(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <NumberInput
                label="Unpaid break"
                value={breakMinutes}
                onChange={(v) => setBreakMinutes(Number(v) || 0)}
                min={0}
                step={15}
                suffix=" min"
                style={{ flex: 1 }}
              />
            </Group>
            {/* The day read back before it is saved — the same reading the server will give it,
                midnight crossing and all, so "18:00 to 02:00" is visibly eight hours on the day
                it started rather than a surprise on the sheet. */}
            <Text size="sm" c={timesProblem ? 'red.7' : 'dimmed'}>
              {timesProblem ??
                (workedMinutes !== null
                  ? `${describeShift(startTime, endTime)}${
                      breakMinutes > 0 ? ` with ${breakMinutes} min break` : ''
                    } = ${formatHours(workedMinutes)} worked.`
                  : 'An end earlier than the start means the shift ran past midnight.')}
            </Text>
          </>
        ) : (
          <TextInput
            label="Hours (optional)"
            placeholder="7:30 or 7.5"
            value={hoursText}
            onChange={(e) => setHoursText(e.currentTarget.value)}
            error={ptoProblem ? 'Write hours as 7:30 or 7.5' : undefined}
            description={
              parsed !== null
                ? `That reads as ${formatHours(parsed)}.`
                : 'Leave blank to pay whatever their schedule expected of this day.'
            }
            autoFocus
          />
        )}
        <Textarea
          label="Note"
          description="Kept with the record — why this day was entered by hand."
          placeholder="Phone died, worked the full shift · Took the day as PTO"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          autosize
          minRows={2}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!date || incomplete} onClick={() => save.mutate()}>
            {existing ? 'Replace the entry' : 'Record it'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
